// Survivor names (CHANGELOG 8.1): 3–16 characters (letters, numbers, space, - and _),
// at least one letter, and a basic swear-word filter. Names do not have to be unique.

export const NAME_RULES = { min: 3, max: 16 } as const;

export const NAME_PATTERN = /^[A-Za-z0-9 _-]+$/;

/** Always blocked, wherever they show up in the name (after undoing l33t and separators). */
const BLOCKED_ANYWHERE = [
  'fuck', 'cunt', 'nigger', 'nigga', 'faggot', 'whore', 'slut', 'hitler', 'retard', 'pussy', 'asshole', 'bitch', 'shit',
  // Bosnian / Croatian / Serbian
  'jebem', 'jebi', 'jebo', 'kurac', 'kurca', 'picka', 'pizda', 'kurva', 'govno', 'drolja',
];

/** Blocked only as a whole word, because they hide inside ordinary names (Dickson, Hancock, Nazif, Therapist). */
const BLOCKED_WORDS = ['dick', 'cock', 'fag', 'cum', 'tits', 'penis', 'rape', 'rapist', 'sex', 'anus', 'nazi', 'jeb', 'sisa', 'peder'];

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g' };

function unleet(s: string): string {
  return s
    .toLowerCase()
    .split('')
    .map((c) => LEET[c] ?? c)
    .join('');
}

/** True when the name contains a blocked word. */
export function hasProfanity(name: string): boolean {
  const plain = unleet(name);
  const squashed = plain.replace(/[^a-z]/g, '');
  if (BLOCKED_ANYWHERE.some((w) => squashed.includes(w))) return true;
  const words = plain.split(/[^a-z]+/).filter(Boolean);
  return words.some((w) => BLOCKED_WORDS.includes(w));
}

export type NameIssue = 'short' | 'long' | 'chars' | 'letter' | 'profanity';

/** The first problem with a name (already trimmed), or null when it is fine. */
export function nameIssue(raw: string): NameIssue | null {
  const name = raw.trim();
  if (name.length < NAME_RULES.min) return 'short';
  if (name.length > NAME_RULES.max) return 'long';
  if (!NAME_PATTERN.test(name)) return 'chars';
  if (!/[A-Za-z]/.test(name)) return 'letter';
  if (hasProfanity(name)) return 'profanity';
  return null;
}

export const NAME_ISSUE_TEXT: Record<NameIssue, string> = {
  short: `At least ${NAME_RULES.min} characters`,
  long: `At most ${NAME_RULES.max} characters`,
  chars: 'Letters, numbers, spaces, - and _ only',
  letter: 'Needs at least one letter',
  profanity: 'That name is not allowed',
};

// ---- Renaming (20. 10. 2026, owner) -------------------------------------------------------------
// The survivor's name can change after creation: the first change is free (any time), after that at most once in
// `cooldownDays`. The same name rules and filter apply. Every change is kept in a log (`name_changes`); fights, wanted
// posters and revenge follow the player by id, so they keep working after a rename.

export const RENAME_RULES = {
  /** Days between two renames after the first (free) one. */
  cooldownDays: 30,
} as const;

/** When the next rename is allowed: null when it is free now (no rename yet), else the last one plus the cooldown. */
export function renameNextAt(count: number, lastAt: number | null | undefined): number | null {
  if (count <= 0 || lastAt == null) return null;
  return lastAt + RENAME_RULES.cooldownDays * 86_400_000;
}

/** Whether a rename is allowed at `now`. */
export function canRename(count: number, lastAt: number | null | undefined, now: number): boolean {
  const next = renameNextAt(count, lastAt);
  return next == null || now >= next;
}
